<script setup>
import { ref, computed, onMounted } from "vue";
import { useDateFilter } from "~/composables/useDateFilter";
import { useBet } from "~/composables/useBet";

definePageMeta({
  layout: "mypage",
});

// ===== Composables =====
const {
  dateOptions: options,
  selectedType,
  selectRef,
  handleSelectChange,
  applyDateFilter,
} = useDateFilter();

const { getBettingHistory } = useBet();

// ===== 상태 =====
const transactionHistory = ref([]);
const isLoading = ref(false);

// ===== API 호출 =====
const loadBettingHistory = async () => {
  isLoading.value = true;
  try {
    const result = await getBettingHistory(100); // 최근 100개
    if (result.success && result.history) {
      transactionHistory.value = result.history.map((item, index) => ({
        id: item.roundId + '-' + item.type + '-' + index, // roundId + type으로 unique ID 생성
        date: item.date, // 날짜 (Y-m-d)
        time: item.time, // 시간 (H:i)
        direction: item.type.toLowerCase(), // type을 direction으로
        betAmount: item.originalAmount, // 배팅금액 (원래금액)
        result: item.result === '승리' ? 'win' : (item.result === '패배' ? 'lose' : 'pending'),
        profit: item.profit || 0,
      }));
      // console.log('[History] Loaded', transactionHistory.value.length, 'records');
    } else {
      console.error('[History] API failed:', result.message);
    }
  } catch (error) {
    console.error('베팅 내역 로드 실패:', error);
  } finally {
    isLoading.value = false;
  }
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  loadBettingHistory();
});

// ===== 계산된 값 =====
// 필터링된 배팅 내역 (날짜 필터 적용)
const filteredTransactionHistory = computed(() => {
  return applyDateFilter(transactionHistory.value);
});

// 총 배팅금액 (모든 배팅 금액의 합계)
const totalBetAmount = computed(() => {
  return filteredTransactionHistory.value.reduce(
    (sum, transaction) => sum + transaction.betAmount,
    0
  );
});

// 총 당첨금액 (승리한 배팅의 profit 합계, 양수만)
const totalWinAmount = computed(() => {
  return filteredTransactionHistory.value
    .filter((transaction) => transaction.result === "win")
    .reduce((sum, transaction) => sum + transaction.profit, 0);
});

// 이익 (모든 profit의 합계)
const totalProfit = computed(() => {
  return filteredTransactionHistory.value.reduce(
    (sum, transaction) => sum + transaction.profit,
    0
  );
});

// 배팅건수
const totalBetCount = computed(() => {
  return filteredTransactionHistory.value.length;
});

// ===== 함수 =====
// 원화 포맷팅 함수 (천 단위 콤마 추가)
const formatKRW = (amount) => {
  return amount.toLocaleString("ko-KR");
};
</script>

<template>
  <div class="mypage__wallet">
    <!-- ===== 필터 영역 ===== -->
    <div
      class="mypage__wallet-item mypage__wallet-item--column mypage__wallet-item--history"
    >
      <div class="mypage__wallet-history-filter">
        <strong class="mypage__wallet-history-title">배팅내역</strong>
        <!-- 날짜 필터 선택 (지난 N일 또는 날짜 범위 선택) -->
        <div class="mypage__wallet-history-filter-select">
          <Select
            ref="selectRef"
            v-model="selectedType"
            :options="options"
            placeholder="전체"
            size="md"
            @change="handleSelectChange"
          />
        </div>
      </div>
    </div>

    <!-- ===== 통계 영역 ===== -->
    <div
      class="mypage__wallet-item mypage__wallet-item--column mypage__wallet-item--history"
    >
      <ul class="mypage__wallet-total">
        <!-- 총 배팅금액 -->
        <li class="mypage__wallet-total-item">
          <span class="mypage__wallet-total-title">총 배팅금액</span>
          <strong class="mypage__wallet-total-value"
            >{{ formatKRW(totalBetAmount) }}원</strong
          >
        </li>
        <!-- 총 당첨금액 (primary 색상) -->
        <li class="mypage__wallet-total-item">
          <span class="mypage__wallet-total-title">총 당첨금액</span>
          <strong
            class="mypage__wallet-total-value mypage__wallet-total-value--primary"
            >{{ formatKRW(totalWinAmount) }}원</strong
          >
        </li>
        <!-- 이익 (양수: primary, 음수: secondary 색상) -->
        <li class="mypage__wallet-total-item">
          <span class="mypage__wallet-total-title">이익</span>
          <strong
            class="mypage__wallet-total-value"
            :class="{
              'mypage__wallet-total-value--primary': totalProfit > 0,
              'mypage__wallet-total-value--secondary': totalProfit < 0,
            }"
            >{{ totalProfit > 0 ? "+" : "" }}
            {{ formatKRW(totalProfit) }}원</strong
          >
        </li>
        <!-- 배팅건수 -->
        <li class="mypage__wallet-total-item">
          <span class="mypage__wallet-total-title">배팅건수</span>
          <strong
            class="mypage__wallet-total-value mypage__wallet-total-value--case"
            >{{ totalBetCount }}건</strong
          >
        </li>
      </ul>
    </div>

    <!-- ===== 테이블 영역 ===== -->
    <div class="mypage__wallet-item mypage__wallet-item--column">
      <div class="table__inner">
        <div class="table__wrap">
          <table class="table table--b">
            <colgroup>
              <col width="160px" />
              <col width="105px" />
              <col width="122px" />
              <col width="80px" />
              <col width="122px" />
            </colgroup>
            <thead class="table__head">
              <tr class="table__row table__row--th">
                <th class="table__cell table__cell--th">날짜</th>
                <th class="table__cell table__cell--th">유형</th>
                <th class="table__cell table__cell--th">배팅금액</th>
                <th class="table__cell table__cell--th">결과</th>
                <th class="table__cell table__cell--th">수익</th>
              </tr>
            </thead>
            <tbody class="table__body">
              <tr
                v-for="transaction in filteredTransactionHistory"
                :key="transaction.id"
                class="table__row table__row--td"
              >
                <!-- 날짜 -->
                <td class="table__cell table__cell--td">
                  <span class="table__cell-date">{{ transaction.date }} {{ transaction.time }}</span>
                </td>
                <!-- 유형 (UP/Down, badge 스타일 적용) -->
                <td class="table__cell table__cell--td">
                  <span
                    v-if="transaction.direction === 'up'"
                    class="badge badge--up badge--md ico-after ico-up"
                    >UP</span
                  >
                  <span
                    v-else-if="transaction.direction === 'down'"
                    class="badge badge--down badge--md ico-after ico-down"
                    >Down</span
                  >
                </td>
                <!-- 배팅금액 (원화 포맷) -->
                <td class="table__cell table__cell--td">
                  <span>{{ formatKRW(transaction.betAmount) }}</span>
                </td>
                <!-- 결과 (승리/손실, primary/secondary 색상 적용) -->
                <td
                  class="table__cell table__cell--td"
                  :class="{
                    'table__cell--primary': transaction.result === 'win',
                    'table__cell--secondary': transaction.result === 'lose',
                  }"
                >
                  <span>{{
                    transaction.result === "win" ? "승리" : "손실"
                  }}</span>
                </td>
                <!-- 수익 (승리: +, primary 색상, 패배: -, secondary 색상, 원화 포맷) -->
                <td
                  class="table__cell table__cell--td"
                  :class="{
                    'table__cell--primary': transaction.result === 'win',
                    'table__cell--secondary': transaction.result === 'lose',
                  }"
                >
                  <span
                    >{{ transaction.profit > 0 ? "+" : ""
                    }}{{ formatKRW(transaction.profit) }}</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
<style></style>
